<script setup lang="ts">
import { CoverFeatureItem, CoverFeatures } from '@ilteducation/components';
import CategoryBubble from '@/components/CategoryBubble.vue';
import { computed, ref } from 'vue';
import { BookWithDuplicates } from '@ilteducation/books';
import type { Category } from '@ilteducation/content-ref-data-types';
import { useBookSearch } from '@/composables/useBookSearch';
import { useGrades } from '@/composables/use-grades';
import { useLocale } from '@/plugins/translation';

const props = withDefaults(
  defineProps<{
    book: BookWithDuplicates;
    showAsStack?: boolean;
    bookSubject?: Category;
  }>(),
  {
    showAsStack: false,
    bookSubject: undefined,
  },
);

const { grades } = useBookSearch();
const { gradeAbbreviation } = useGrades();
const { t, l } = useLocale();

const bookGrades = computed(() => grades.value.filter((g) => props.book.categories?.includes(g.id)));

const features = computed(() => {
  const features: CoverFeatureItem[] = [];
  if (props.bookSubject) {
    features.push({
      id: 'category',
      type: 'category',
      tooltip: props.bookSubject.category,
      tooltipLocation: 'end',
      data: props.bookSubject,
    });
  }
  features.push(
    ...bookGrades.value.map(
      (grade): CoverFeatureItem => ({
        id: grade.id,
        type: 'grade',
        tooltip: grade.category,
        tooltipLocation: 'end',
        content: gradeAbbreviation(grade),
      }),
    ),
  );
  if (props.showAsStack) {
    features.push({
      id: 'languages',
      type: 'languages',
      color: 'rgb(var(--v-theme-secondary-container))',
      tooltip: t(l.library.available_in_multiple_languages),
      tooltipLocation: 'end',
    });
  }
  return features;
});

const categoryBubble = ref();
</script>

<template>
  <CoverFeatures
    :features="features"
    direction="vertical"
    position="left"
    :icon-size="20"
    size="large"
    :border="true"
    :extra-item-classes="['rounded-circle', 'feature-border', 'bg-secondary-container']"
  >
    <template #category="{ feature }">
      <v-tooltip :activator="categoryBubble" aria-hidden="true">
        {{ feature.tooltip }}
      </v-tooltip>
      <li>
        <CategoryBubble
          ref="categoryBubble"
          class="book-feature elevation-1 direction-vertical"
          :subject="feature.data"
          size="small"
        />
      </li>
    </template>
  </CoverFeatures>
</template>

<style scoped lang="scss"></style>
